/**
 * "Parents" section of the Preset detail page - the companion parent group.
 *
 * One component renders all three states of a companion pair, because they are
 * the same relationship seen from either end:
 *
 *   1. SOURCE WITHOUT A COMPANION - offer to add one (site-admin only).
 *   2. SOURCE WITH A COMPANION    - name, counts, open, unlink, visibility.
 *   3. THE COMPANION ITSELF       - derived banner, link back, visibility.
 *
 * Both halves of the pair are ordinary presets, so the other half is read with
 * the same `usePresetDetail` query the page itself uses; nothing here needs a
 * bespoke endpoint.
 *
 * Two contract facts drive most of the rendering decisions below:
 *
 *   - A companion's membership is COMPUTED. `derived_from` being set is the
 *     signal to suppress definition editing entirely - the hourly materialiser
 *     re-derives the roster, so a hand-edit would be reverted within the hour
 *     with no error shown to whoever made it.
 *   - A companion is created with its member list hidden, and pymble-apis
 *     SUPPRESSES `member_count` to 0 whenever the effective member-list
 *     visibility is false. So a hidden companion's derived count is not
 *     "zero carers", it is "not disclosed" - rendering the raw 0 would invent
 *     the exact bug the separate-counts requirement exists to prevent.
 *
 * Site-admin only, matching the rest of the companion surface: a bulk carer
 * expansion is a contact-enumeration surface. Counts and preset names only -
 * no contact ids, no member names.
 */
import { useState } from "react";
import { IonAlert, IonButton, IonIcon, IonInput } from "@ionic/react";
import {
  arrowUpOutline,
  eyeOffOutline,
  eyeOutline,
  gitBranchOutline,
  linkOutline,
  openOutline,
  peopleOutline,
  unlinkOutline,
  warningOutline,
} from "ionicons/icons";
import { useHistory } from "react-router-dom";
import { displayMsg } from "pymble-ui/components";
import {
  useAddPresetCompanion,
  usePresetDetail,
  useUnlinkPresetCompanion,
  useUpdatePreset,
} from "../../hooks/usePresets";
import {
  defaultCompanionNameLong,
  defaultCompanionNameShort,
} from "./companionNaming";
import { formatMemberCount, memberCountDisclosure } from "./memberCount";
import { parsePresetError } from "./presetErrorParser";
import {
  PRESET_NAME_LONG_MAX,
  PRESET_NAME_SHORT_MAX,
  type Preset,
} from "../../types/preset";

interface PresetCompanionSectionProps {
  preset: Preset;
  /** True for site-admins. Read-only rendering otherwise: the companion
   *  lifecycle is admin-only, but a manager still needs to understand why a
   *  derived preset has no editing affordances. */
  canManage: boolean;
}

/** Copy explaining why the two counts legitimately differ. Shown wherever both
 *  numbers are on screen, so a smaller parent count never reads as data loss. */
const COUNT_EXPLAINER =
  "Fewer parents than members is normal: siblings share the same carers, and " +
  "members who are not students contribute none.";


export function PresetCompanionSection({
  preset,
  canManage,
}: PresetCompanionSectionProps) {
  const history = useHistory();

  const derivedFrom = preset.derived_from ?? null;
  const isCompanion = !!derivedFrom;
  const companionId = preset.companion_preset_id ?? null;

  // The other half of the pair, whichever end we are standing on. Undefined
  // when the preset is not part of a pair, which leaves the query disabled.
  const linkedId = derivedFrom?.preset_id ?? companionId ?? undefined;
  const linked = usePresetDetail(linkedId);
  const linkedPreset = linked.data ?? null;

  // The companion is the half whose visibility we manage: when viewing the
  // companion that is this preset, when viewing the source it is the linked one.
  const companionPreset = isCompanion ? preset : linkedPreset;
  const visibilityTargetId = isCompanion ? preset.id : companionId ?? undefined;

  const addCompanion = useAddPresetCompanion(preset.id);
  const unlinkCompanion = useUnlinkPresetCompanion(preset.id);
  const updateVisibility = useUpdatePreset(visibilityTargetId);

  const [nameShort, setNameShort] = useState<string | null>(null);
  const [nameLong, setNameLong] = useState<string | null>(null);
  const [unlinkConfirm, setUnlinkConfirm] = useState(false);
  const [revealConfirm, setRevealConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Every piece of state above is scoped to ONE preset, but this component is
  // rendered from a `/presets/:id` route and the section's own "Open parent
  // group" / "Open source preset" buttons move between ids WITHOUT changing the
  // route pattern - so React Router reuses the instance and none of it would
  // reset. A half-typed companion name following the operator to another
  // preset is the mild version; an open confirmation firing its handler
  // against a different preset is the one that matters. Adjusting state during
  // render is React's documented pattern for this and avoids rendering a frame
  // of the previous preset's state.
  const [scopedToId, setScopedToId] = useState(preset.id);
  if (scopedToId !== preset.id) {
    setScopedToId(preset.id);
    setNameShort(null);
    setNameLong(null);
    setUnlinkConfirm(false);
    setRevealConfirm(false);
    setError(null);
  }

  const nameShortValue =
    nameShort ?? defaultCompanionNameShort(preset.name_short);
  const nameLongValue =
    nameLong ?? defaultCompanionNameLong(preset.name_long || preset.name_short);

  const showError = (err: unknown, fallback: string) => {
    const parsed = parsePresetError(err);
    const title = parsed?.title ?? fallback;
    setError(title);
    displayMsg(title, { type: "danger", position: "top", duration: 3000 });
  };

  const handleAdd = async () => {
    setError(null);
    const short = nameShortValue.trim();
    if (!short) {
      setError("Name the parent group before adding it");
      return;
    }
    try {
      await addCompanion.mutateAsync({
        companion_name_short: short,
        companion_name_long: nameLongValue.trim() || short,
      });
      displayMsg("Companion parent group added", {
        type: "success",
        position: "top",
        duration: 1500,
      });
    } catch (err) {
      showError(err, "Failed to add the companion parent group");
    }
  };

  const handleUnlink = async () => {
    setError(null);
    try {
      await unlinkCompanion.mutateAsync(companionId ?? undefined);
      displayMsg("Companion parent group unlinked and deleted", {
        type: "success",
        position: "top",
        duration: 1500,
      });
    } catch (err) {
      showError(err, "Failed to unlink the companion parent group");
    }
  };

  const handleVisibility = async (reveal: boolean) => {
    setError(null);
    try {
      await updateVisibility.mutateAsync({ show_member_list: reveal });
      displayMsg(
        reveal ? "Parent group member list revealed" : "Parent group member list hidden",
        { type: "success", position: "top", duration: 1500 }
      );
    } catch (err) {
      showError(err, "Failed to change the member list visibility");
    }
  };

  // Standing on the source, the companion arrives via its own query, so it can
  // be in flight. Standing on the companion, it is the preset we were handed
  // and is always present.
  const companionLoading = !isCompanion && !!companionId && linked.isLoading;
  const companionDisclosure = companionPreset
    ? memberCountDisclosure(companionPreset)
    : null;

  // Nothing to show for a personal preset that a non-admin is looking at.
  if (!isCompanion && !companionId && !(canManage && preset.audience === "shared")) {
    return null;
  }

  const errorBanner = error && (
    <div className="preset-modal-error" role="alert">
      <strong>{error}</strong>
    </div>
  );

  // A failed lookup of the other half must not read as "there is nothing
  // there". Once retries stop, `isLoading` goes false with the data still
  // null, which is indistinguishable from an empty result unless we say so -
  // and the operator would then be looking at an unnamed group with a live
  // Unlink button beside it.
  const linkedFailed = linked.isError && !linkedPreset;
  const linkedErrorBanner = linkedFailed && (
    <div className="preset-modal-error" role="alert">
      <strong>
        {isCompanion
          ? "This group's source preset could not be loaded."
          : "This preset's parent group could not be loaded."}
      </strong>
      <p style={{ margin: "6px 0 0" }}>
        Its name and member count are unavailable, so actions that depend on
        it are disabled. Refresh to try again.
      </p>
    </div>
  );

  // Nothing gates the companion checkbox on the source having student
  // members, and gating it would be the wrong fix anyway - membership changes
  // long after creation, so a companion that derives correctly today can
  // derive nothing tomorrow. Say so when it happens instead.
  //
  // Only assertable when the count is actually DISCLOSED. A hidden companion's
  // 0 is a suppressed value, not an empty roster, and warning on it would
  // invent the alarm for every companion (they are all created hidden).
  const companionDerivedNothing =
    companionDisclosure === "visible" && companionPreset?.member_count === 0;

  const emptyDerivationWarning = companionDerivedNothing && (
    <div className="preset-companion-warning" role="status">
      <IonIcon icon={warningOutline} aria-hidden="true" />
      <span>
        This parent group has no members. Carers are derived from the source
        preset's STUDENT members, so a source made up of staff or of groups
        with no students derives nobody. Anything sent to this group reaches
        no one until students are added to the source and it is materialised
        again.
      </span>
    </div>
  );

  // Never assert a visibility state we have not established: while the
  // companion query is in flight there is nothing to say, and with no override
  // saved and no successful run the effective value is genuinely unknown. The
  // control still resolves it - saving `false` is both the safe direction for a
  // parent group and what makes the state definite from then on.
  const visibilityRow = visibilityTargetId && companionDisclosure && (
    <div className="preset-companion-visibility">
      <IonIcon
        icon={companionDisclosure === "visible" ? eyeOutline : eyeOffOutline}
        aria-hidden="true"
      />
      <span className="preset-companion-visibility-text">
        {companionDisclosure === "hidden" &&
          "Member list hidden. Parents cannot see who else is in this group, and the derived count is not shown here."}
        {companionDisclosure === "visible" &&
          "Member list visible. Every parent in this group can see the other families in it."}
        {companionDisclosure === "unknown" &&
          "Member list visibility has not been set for this group. Until it is, the materialiser decides it on each run and the derived count cannot be trusted."}
      </span>
      {canManage && (
        <IonButton
          fill="outline"
          size="small"
          disabled={updateVisibility.isPending}
          onClick={() =>
            companionDisclosure === "hidden"
              ? setRevealConfirm(true)
              : handleVisibility(false)
          }
        >
          {companionDisclosure === "hidden"
            ? "Reveal member list"
            : "Hide member list"}
        </IonButton>
      )}
    </div>
  );

  // ---- 3. Viewing the companion itself -------------------------------------
  if (isCompanion) {
    const sourceName = linked.isLoading
      ? "Loading..."
      : linkedPreset?.name_short ?? "its source preset";
    return (
      <div className="preset-detail-section preset-companion-card">
        <div className="preset-detail-section-head preset-companion-head">
          <span className="preset-detail-section-badge">
            <IonIcon icon={gitBranchOutline} aria-hidden="true" />
          </span>
          <span className="preset-detail-section-heading">
            <span className="preset-detail-section-title">
              Derived parent group
            </span>
            <span className="preset-detail-section-subtitle">
              Members are the carers of {sourceName}'s student members
            </span>
          </span>
        </div>
        <div className="preset-detail-section-body">
          {errorBanner}
          {linkedErrorBanner}
          <p className="preset-companion-note">
            This group's membership is computed, not authored. It is rebuilt
            every time the source preset is materialised, so it cannot be
            edited by hand - any change would be reverted on the next run.
          </p>
          <div className="preset-companion-counts">
            <span className="preset-companion-count">
              <IonIcon icon={peopleOutline} aria-hidden="true" />
              Source members: {formatMemberCount(linkedPreset, linked.isLoading)}
            </span>
            <span className="preset-companion-count">
              <IonIcon icon={peopleOutline} aria-hidden="true" />
              Parents (derived): {formatMemberCount(preset, false)}
            </span>
          </div>
          <p className="preset-companion-note">{COUNT_EXPLAINER}</p>
          {emptyDerivationWarning}
          {visibilityRow}
          <div className="preset-companion-actions">
            <IonButton
              fill="outline"
              size="small"
              disabled={!linkedPreset}
              onClick={() =>
                linkedPreset && history.push(`/presets/${linkedPreset.id}`)
              }
            >
              <IonIcon slot="start" icon={arrowUpOutline} />
              Open source preset
            </IonButton>
          </div>
        </div>
        <IonAlert
          isOpen={revealConfirm}
          onDidDismiss={() => setRevealConfirm(false)}
          header="Reveal the member list"
          message="Every parent in this group will be able to see the other families in it, which can disclose why the source cohort exists. You can hide it again afterwards."
          buttons={[
            { text: "Cancel", role: "cancel" },
            { text: "Reveal", handler: () => handleVisibility(true) },
          ]}
        />
      </div>
    );
  }

  // ---- 2. Source that already has a companion ------------------------------
  if (companionId) {
    return (
      <div className="preset-detail-section preset-companion-card">
        <div className="preset-detail-section-head preset-companion-head">
          <span className="preset-detail-section-badge">
            <IonIcon icon={linkOutline} aria-hidden="true" />
          </span>
          <span className="preset-detail-section-heading">
            <span className="preset-detail-section-title">
              Parents
            </span>
            <span className="preset-detail-section-subtitle">
              A linked group of the carers of this preset's student members
            </span>
          </span>
        </div>
        <div className="preset-detail-section-body">
          {errorBanner}
          {linkedErrorBanner}
          <p className="preset-companion-name">
            {linked.isLoading
              ? "Loading parent group..."
              : linkedPreset?.name_short ?? "Parent group"}
          </p>
          <div className="preset-companion-counts">
            <span className="preset-companion-count">
              <IonIcon icon={peopleOutline} aria-hidden="true" />
              Source members: {formatMemberCount(preset, false)}
            </span>
            <span className="preset-companion-count">
              <IonIcon icon={peopleOutline} aria-hidden="true" />
              Parents (derived): {formatMemberCount(linkedPreset, companionLoading)}
            </span>
          </div>
          <p className="preset-companion-note">{COUNT_EXPLAINER}</p>
          {emptyDerivationWarning}
          {visibilityRow}
          <div className="preset-companion-actions">
            <IonButton
              fill="outline"
              size="small"
              onClick={() => history.push(`/presets/${companionId}`)}
            >
              <IonIcon slot="start" icon={openOutline} />
              Open parent group
            </IonButton>
            {canManage && (
              // Unlink DELETES the group it names. Withhold it until we can
              // actually name it - confirming the deletion of an unnamed
              // group is not informed consent.
              <IonButton
                fill="outline"
                color="danger"
                size="small"
                disabled={unlinkCompanion.isPending || !linkedPreset}
                onClick={() => setUnlinkConfirm(true)}
              >
                <IonIcon slot="start" icon={unlinkOutline} />
                Unlink
              </IonButton>
            )}
          </div>
        </div>
        <IonAlert
          isOpen={unlinkConfirm}
          onDidDismiss={() => setUnlinkConfirm(false)}
          header="Unlink the parent group"
          message="Unlinking DELETES the parent group - it is soft-deleted, not kept. Anyone targeting it loses that audience immediately. You can add a new parent group afterwards, but its membership will be rebuilt from scratch."
          buttons={[
            { text: "Cancel", role: "cancel" },
            {
              text: "Unlink and delete",
              role: "destructive",
              handler: handleUnlink,
            },
          ]}
        />
        <IonAlert
          isOpen={revealConfirm}
          onDidDismiss={() => setRevealConfirm(false)}
          header="Reveal the member list"
          message="Every parent in this group will be able to see the other families in it, which can disclose why the source cohort exists. You can hide it again afterwards."
          buttons={[
            { text: "Cancel", role: "cancel" },
            { text: "Reveal", handler: () => handleVisibility(true) },
          ]}
        />
      </div>
    );
  }

  // ---- 1. Source with no companion yet -------------------------------------
  return (
    <div className="preset-detail-section preset-companion-card">
      <div className="preset-detail-section-head preset-companion-head">
        <span className="preset-detail-section-badge">
          <IonIcon icon={peopleOutline} aria-hidden="true" />
        </span>
        <span className="preset-detail-section-heading">
          <span className="preset-detail-section-title">Parents</span>
          <span className="preset-detail-section-subtitle">
            No companion parent group for this preset
          </span>
        </span>
      </div>
      <div className="preset-detail-section-body">
        {errorBanner}
        <p className="preset-companion-note">
          A companion parent group is a second, linked preset whose members are
          the carers of this preset's student members. It is rebuilt whenever
          this preset's membership changes, and its member list starts hidden.
        </p>
        <div className="preset-grid-2">
          <div className="preset-col">
            <div className="preset-modal-field">
              <span className="preset-modal-field-label">
                Parent group name (short)
              </span>
              <IonInput
                className="preset-text-input"
                fill="outline"
                aria-label="Parent group name (short)"
                value={nameShortValue}
                onIonInput={(e) => setNameShort(e.detail.value ?? "")}
                maxlength={PRESET_NAME_SHORT_MAX}
              />
            </div>
          </div>
          <div className="preset-col">
            <div className="preset-modal-field">
              <span className="preset-modal-field-label">
                Parent group name (long)
              </span>
              <IonInput
                className="preset-text-input"
                fill="outline"
                aria-label="Parent group name (long)"
                value={nameLongValue}
                onIonInput={(e) => setNameLong(e.detail.value ?? "")}
                maxlength={PRESET_NAME_LONG_MAX}
              />
            </div>
          </div>
        </div>
        <div className="preset-companion-actions">
          <IonButton
            fill="outline"
            size="small"
            disabled={addCompanion.isPending}
            onClick={handleAdd}
          >
            <IonIcon slot="start" icon={linkOutline} />
            {addCompanion.isPending
              ? "Adding..."
              : "Add companion parent group"}
          </IonButton>
        </div>
      </div>
    </div>
  );
}
